import React, { useEffect, useState } from "react";
import { api } from "../api/client";

type DinnerOption = { id: string; label: string };
type PublicData = {
  dinnerOptions: DinnerOption[];
  rsvpByDate: string | null;
};

type Guest = {
  id: string;
  first_name: string;
  last_name: string;
  plus_one: boolean;
  plus_one_first_name: string | null;
  plus_one_last_name: string | null;
  rsvp_status: string;
  plus_one_rsvp_status: string;
  dinner_choice_id: string | null;
  plus_one_dinner_choice_id: string | null;
  children: ChildGuest[];
};

type ChildGuest = {
  id: string;
  first_name: string;
  last_name: string;
  rsvp_status: string;
  dinner_choice_id: string | null;
};

type ChildResponse = { attending: boolean | null; dinnerId: string };

export default function Rsvp() {
  const [publicData, setPublicData] = useState<PublicData | null>(null);
  const [first, setFirst] = useState("");
  const [last, setLast] = useState("");
  const [lookupErr, setLookupErr] = useState<string | null>(null);
  const [guest, setGuest] = useState<Guest | null>(null);
  const [isPlusOne, setIsPlusOne] = useState(false);

  const [attending, setAttending] = useState<boolean | null>(null);
  const [dinnerId, setDinnerId] = useState<string>("");
  const [plusOneAttending, setPlusOneAttending] = useState<boolean | null>(null);
  const [plusOneDinnerId, setPlusOneDinnerId] = useState<string>("");
  const [childResponses, setChildResponses] = useState<Record<string, ChildResponse>>({});
  const [submitStatus, setSubmitStatus] = useState<null | "ok" | "err">(null);
  const [submitMsg, setSubmitMsg] = useState<string>("");

  useEffect(() => {
    api.get<PublicData>("/api/public").then(setPublicData).catch(() => {});
  }, []);

  async function onLookup(e: React.FormEvent) {
    e.preventDefault();
    setLookupErr(null);
    setGuest(null);
    setSubmitStatus(null);
    setIsPlusOne(false);
    setChildResponses({});
    try {
      const r = await api.get<{ guest: Guest; isPlusOne: boolean }>(
        `/api/rsvp/lookup?first=${encodeURIComponent(first)}&last=${encodeURIComponent(last)}`
      );
      setGuest(r.guest);
      setIsPlusOne(r.isPlusOne);
      
      // Set primary guest RSVP status
      if (r.guest.rsvp_status === "attending") setAttending(true);
      else if (r.guest.rsvp_status === "declined") setAttending(false);
      else setAttending(null);
      setDinnerId(r.guest.dinner_choice_id ?? "");
      
      // Set plus one RSVP status (if they have a plus one)
      if (r.guest.plus_one) {
        if (r.guest.plus_one_rsvp_status === "attending") setPlusOneAttending(true);
        else if (r.guest.plus_one_rsvp_status === "declined") setPlusOneAttending(false);
        else setPlusOneAttending(null);
      setPlusOneDinnerId(r.guest.plus_one_dinner_choice_id ?? "");
      }
      setChildResponses(
        Object.fromEntries(
          (r.guest.children ?? []).map((child) => [
            child.id,
            {
              attending:
                child.rsvp_status === "attending"
                  ? true
                  : child.rsvp_status === "declined"
                    ? false
                    : null,
              dinnerId: child.dinner_choice_id ?? "",
            },
          ])
        )
      );
    } catch (err: any) {
      setLookupErr(
        "We couldn't find your name on the invite list. Please double-check the spelling."
      );
    }
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!guest || attending === null) return;
    if (attending && !dinnerId) {
      setSubmitStatus("err");
      setSubmitMsg(`Please pick a dinner option for ${guest.first_name}.`);
      return;
    }
    if (guest.plus_one && plusOneAttending === null) {
      setSubmitStatus("err");
      setSubmitMsg("Please indicate whether your plus one will be attending.");
      return;
    }
    if (guest.plus_one && plusOneAttending && !plusOneDinnerId) {
      setSubmitStatus("err");
      const plusOneName = guest.plus_one_first_name || "your plus one";
      setSubmitMsg(`Please pick a dinner option for ${plusOneName}.`);
      return;
    }
    for (const child of guest.children ?? []) {
      const response = childResponses[child.id];
      if (!response || response.attending === null) {
        setSubmitStatus("err");
        setSubmitMsg(`Please indicate whether ${child.first_name} will be attending.`);
        return;
      }
      if (response.attending && !response.dinnerId) {
        setSubmitStatus("err");
        setSubmitMsg(`Please pick a dinner option for ${child.first_name}.`);
        return;
      }
    }
    try {
      await api.post("/api/rsvp", {
        guestId: guest.id,
        attending,
        dinnerChoiceId: attending ? dinnerId : null,
        plusOneAttending: guest.plus_one ? plusOneAttending : null,
        plusOneDinnerChoiceId: guest.plus_one && plusOneAttending ? plusOneDinnerId : null,
        children: (guest.children ?? []).map((child) => ({
          id: child.id,
          attending: childResponses[child.id].attending,
          dinnerChoiceId: childResponses[child.id].attending
            ? childResponses[child.id].dinnerId
            : null,
        })),
      });
      setSubmitStatus("ok");
      const anyAttending = attending || plusOneAttending || (guest.children ?? []).some(
        (child) => childResponses[child.id]?.attending
      );
      setSubmitMsg(
        anyAttending
          ? "Thank you! We can't wait to celebrate with you."
          : "Thanks for letting us know — we'll miss you!"
      );
    } catch (err: any) {
      setSubmitStatus("err");
      setSubmitMsg(err.message || "Something went wrong.");
    }
  }

  return (
    <div className="rsvp-card">
      <h1>RSVP</h1>
      {publicData?.rsvpByDate && (
        <p className="muted">
          Please respond by{" "}
          {new Date(publicData.rsvpByDate).toLocaleDateString(undefined, {
            month: "long",
            day: "numeric",
            year: "numeric",
          })}
          .
        </p>
      )}

      {!guest && (
        <form onSubmit={onLookup} className="rsvp-anchor">
          <label>
            First name
            <input
              value={first}
              onChange={(e) => setFirst(e.target.value)}
              required
            />
          </label>
          <label>
            Last name
            <input
              value={last}
              onChange={(e) => setLast(e.target.value)}
              required
            />
          </label>
          <button type="submit" className="button">
            Find my invite
          </button>
          {lookupErr && <p className="error">{lookupErr}</p>}
        </form>
      )}

      {guest && submitStatus !== "ok" && (
        <form onSubmit={onSubmit} className="rsvp-form">
          <p>
            Hi{" "}
            <strong>
              {isPlusOne ? guest.plus_one_first_name : guest.first_name}
            </strong>
            ! Please let us know who will be joining us.
          </p>

          {/* Primary guest RSVP */}
          <div className="rsvp-person">
            <h3>{guest.first_name} {guest.last_name}</h3>
            <div className="choice">
              <label>
                <input
                  type="radio"
                  name="attending"
                  checked={attending === true}
                  onChange={() => setAttending(true)}
                />{" "}
                Joyfully accepts
              </label>
              <label>
                <input
                  type="radio"
                  name="attending"
                  checked={attending === false}
                  onChange={() => setAttending(false)}
                />{" "}
                Regretfully declines
              </label>
            </div>
            {attending && (
              <label>
                Dinner selection
                <select
                  value={dinnerId}
                  onChange={(e) => setDinnerId(e.target.value)}
                >
                  <option value="">Choose one…</option>
                  {publicData?.dinnerOptions.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </div>

          {/* Plus one RSVP */}
          {guest.plus_one && (
            <div className="rsvp-person">
              <h3>
                {guest.plus_one_first_name
                  ? `${guest.plus_one_first_name}${guest.plus_one_last_name ? ` ${guest.plus_one_last_name}` : ""}`
                  : "Plus One"}
              </h3>
              <div className="choice">
                <label>
                  <input
                    type="radio"
                    name="plusOneAttending"
                    checked={plusOneAttending === true}
                    onChange={() => setPlusOneAttending(true)}
                  />{" "}
                  Joyfully accepts
                </label>
                <label>
                  <input
                    type="radio"
                    name="plusOneAttending"
                    checked={plusOneAttending === false}
                    onChange={() => setPlusOneAttending(false)}
                  />{" "}
                  Regretfully declines
                </label>
              </div>
              {plusOneAttending && (
                <label>
                  Dinner selection
                  <select
                    value={plusOneDinnerId}
                    onChange={(e) => setPlusOneDinnerId(e.target.value)}
                  >
                    <option value="">Choose one…</option>
                    {publicData?.dinnerOptions.map((o) => (
                      <option key={o.id} value={o.id}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </label>
              )}
            </div>
          )}

          {(guest.children ?? []).map((child) => {
            const response = childResponses[child.id];
            return (
              <div className="rsvp-person" key={child.id}>
                <h3>{child.first_name} {child.last_name}</h3>
                <div className="choice">
                  <label>
                    <input
                      type="radio"
                      name={`child-${child.id}-attending`}
                      checked={response?.attending === true}
                      onChange={() => setChildResponses((current) => ({
                        ...current,
                        [child.id]: { ...current[child.id], attending: true },
                      }))}
                    /> Joyfully accepts
                  </label>
                  <label>
                    <input
                      type="radio"
                      name={`child-${child.id}-attending`}
                      checked={response?.attending === false}
                      onChange={() => setChildResponses((current) => ({
                        ...current,
                        [child.id]: { ...current[child.id], attending: false },
                      }))}
                    /> Regretfully declines
                  </label>
                </div>
                {response?.attending && (
                  <label>
                    Dinner selection
                    <select
                      value={response.dinnerId}
                      onChange={(e) => setChildResponses((current) => ({
                        ...current,
                        [child.id]: { ...current[child.id], dinnerId: e.target.value },
                      }))}
                    >
                      <option value="">Choose one…</option>
                      {publicData?.dinnerOptions.map((o) => (
                        <option key={o.id} value={o.id}>{o.label}</option>
                      ))}
                    </select>
                  </label>
                )}
              </div>
            );
          })}

          <button
            type="submit"
            className="button"
            disabled={
              attending === null ||
              (guest.plus_one && plusOneAttending === null) ||
              (guest.children ?? []).some((child) => childResponses[child.id]?.attending === null)
            }
          >
            Submit RSVP
          </button>
          {submitStatus === "err" && <p className="error">{submitMsg}</p>}
        </form>
      )}

      {submitStatus === "ok" && <p className="success">{submitMsg}</p>}
    </div>
  );
}
